import { ProjectContext } from "context/ProjectProvider";
import { useContext, useEffect, useState } from "react";

import { InboxOutlined } from "@ant-design/icons";
import {
  Form,
  FormInstance,
  Input,
  message,
  UploadFile,
  UploadProps
} from "antd";
import FormItem from "antd/es/form/FormItem";
import { RcFile } from "antd/lib/upload";
import Dragger from "antd/lib/upload/Dragger";

import { BASE_URL } from "services/endpoints";
import { DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import CIQDatePicker from "components/custom-date-picker";
import StepsLayout from "../layout-steps";

export default function ScheduleStep({
  importScheduleForm: form,
  setButtonEnable
}: {
  importScheduleForm: FormInstance<any>;
  setButtonEnable: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { tokenRetrievalState } = useContext(ProjectContext);
  const acceptedExtensions = ["xml", "xer", "mpp", "csv"];

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [unsupportedFileSelected, setUnsupportedFileSelected] = useState(false);

  useEffect(() => {
    setButtonEnable(!(uploading || fileList.length === 0));
  }, [fileList.length, setButtonEnable, uploading]);

  const handleUpload = () => {
    const formData = new FormData();
    fileList.forEach((file) => {
      const extension =
        file?.name?.substring(
          file.name.lastIndexOf(".") + 1,
          file?.name?.length
        ) || "";

      let fileType = "P6";
      if (extension === "mpp") {
        fileType = "MSP";
      } else if (extension === "csv") {
        fileType = "CSV";
      }
      formData.append("file", file as RcFile);
      formData.append("schedule_name", form.getFieldValue("scheduleName"));
      formData.append("file_type", fileType);
      formData.append(
        "schedule_date",
        form.getFieldValue("scheduleDate").format(DATE_FORMAT_MMDDYYYY)
      );
    });

    setUploading(true);
    fetch(`${BASE_URL}/scheduler/import`, {
      method: "POST",
      body: formData,
      headers: {
        authorization: `Bearer ${tokenRetrievalState.token}`
      }
    })
      .then((res) => res.json())
      .then((result) => {
        console.log("Result:", result);
        if (result.success) {
          // message.success(result.success.message);
          console.log(result.success.message);
        } else {
          message.error(result.message ?? "import failed.", 3);
        }
      })
      .catch(() => {
        message.error("import failed.");
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const props: UploadProps = {
    style: { borderColor: unsupportedFileSelected === true ? "red" : "" },
    accept: "text/xml, .xer, .mpp, .csv",
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    onDrop: (e) => {
      if (e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        const fileName = file.name.toLowerCase();
        const extension =
          fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length) ||
          "";
        if (!acceptedExtensions.some((x) => x === extension)) {
          setUnsupportedFileSelected(true);
          setFileList([]);
          message.error("Please select .XML .XER .MPP and .CSV files only");
          return false;
        }
        setUnsupportedFileSelected(false);
        return false;
      }
      return false;
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      const extension =
        fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length) ||
        "";
      if (!acceptedExtensions.some((x) => x === extension)) {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error("Please select .XML .XER .MPP and .CSV files only");
        return false;
      }
      setUnsupportedFileSelected(false);
      setFileList([file]);
      return false;
    },
    showUploadList: {
      showDownloadIcon: false,
      showPreviewIcon: false
    }
  };

  return (
    <StepsLayout title="Upload a Schedule">
      <div className="space-y-4  px-3">
        <div className="w-[437px]">
          <Dragger {...props} disabled={uploading} className="!h-[219px]">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            {fileList.length > 0 ? (
              <p className="ant-upload-text">{fileList[0].name}</p>
            ) : (
              <div>
                <p className="ant-upload-text">
                  Click or drag a file to upload
                </p>
                <p className="ant-upload-hint">
                  Only .csv .xml, .xer and .mpp files are supported.
                </p>
              </div>
            )}
          </Dragger>
        </div>
        <Form form={form} layout="vertical" onFinish={handleUpload}>
          <div className="flex space-x-2">
            <FormItem
              name="scheduleName"
              label="Schedule Name"
              className="w-1/2 placeholder:text-xs"
              hidden={fileList.length === 0}
              rules={[
                {
                  required: true,
                  message: "Enter schedule name"
                }
              ]}
            >
              <Input
                className="h-9 border-solid border border-ciq-gray-800 border-opacity-30 placeholder:text-xs"
                placeholder="Enter schedule name"
                disabled={uploading}
              />
            </FormItem>
            <FormItem
              name="scheduleDate"
              label="Schedule Date"
              className="w-1/2 placeholder:text-xs"
              hidden={fileList.length === 0}
              rules={[
                {
                  type: "object",
                  required: true,
                  message: "Select a date"
                }
              ]}
            >
              <CIQDatePicker
                className="w-full h-9 border-solid border border-ciq-gray-800 border-opacity-30 date-picker-with-icon"
                placeholder="Select schedule date"
                format={DATE_FORMAT_MMDDYYYY}
                disabled={uploading}
              />
            </FormItem>
          </div>
        </Form>
      </div>
    </StepsLayout>
  );
}
